import React, { createContext, useContext, ReactNode } from 'react';
import { useNotifications } from '../hooks/useNotifications';
import { Notification } from '../api/notifications';
import { useAuth } from './AuthContext';
import { useLocation } from 'react-router-dom';

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  error: string | null;
  markAsRead: (id: number) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  deleteNotification: (id: number) => Promise<void>;
  refreshNotifications: () => Promise<void>;
}

const NotificationContext = createContext<NotificationContextType | null>(null);

interface NotificationProviderProps {
  children: ReactNode;
  enableRealTime?: boolean;
  pollingInterval?: number;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({
  children,
  enableRealTime = true,
  pollingInterval = 30000
}) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  const publicRoutes = [
    '/',
    '/login',
    '/signup',
    '/terms-of-service',
    '/privacy-policy',
    '/cookie-policy',
    '/gdpr-policy'
  ];

  const isPublicRoute = publicRoutes.includes(location.pathname);
  const notificationsEnabled = !loading && !!user && !isPublicRoute;

  const notificationData = useNotifications({
    enabled: notificationsEnabled,
    enableRealTime,
    pollingInterval
  });

  return (
    <NotificationContext.Provider value={notificationData}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotificationContext = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotificationContext must be used within a NotificationProvider');
  }
  return context;
};

export const useOptionalNotificationContext = (): NotificationContextType | null => {
  return useContext(NotificationContext);
};
